import { Link, Message, Text } from '@kolonialno/lama';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useCSRFToken } from '../../utils/csrf';
import { useFormErrors } from '../../utils/viewArgs';
import Container from '../components/Container';
import { useAuthenticationContext } from '../utils/context';

import NoMatch from './NoMatch';

function Redirect() {
    const csrf = useCSRFToken();
    const context = useAuthenticationContext();
    const formErrors = useFormErrors();

    const hasErrors = Object.keys(formErrors).length > 0;

    const formRef = useRef<HTMLFormElement>(null);

    const [countdown, setCountdown] = useState(5);

    const redirectUserToDestination = useCallback(() => {
        if (!context.redirect) {
            return;
        }

        formRef.current?.requestSubmit();
    }, [context.redirect]);

    useEffect(() => {
        if (!context.redirect) {
            return;
        }

        if (countdown === 0) {
            redirectUserToDestination();
            return;
        }

        const timer = setTimeout(() => {
            setCountdown(countdown - 1);
        }, 1000);

        return () => clearTimeout(timer);
    }, [countdown, context.redirect, redirectUserToDestination]);

    // If the redirect is not set, show the no match page
    if (!context.redirect) {
        return <NoMatch />;
    }

    return (
        <Container
            title={`Redirecting in ${countdown} second${countdown !== 1 ? 's' : ''}...`}
        >
            {hasErrors && (
                <Message variant="danger">
                    Could not redirect you to the destination.
                </Message>
            )}
            <form method="post" ref={formRef} style={{ textAlign: 'center' }}>
                <input type="hidden" name="csrfmiddlewaretoken" value={csrf} />
                <input type="hidden" name="rd" value={context.redirect} />

                <Text
                    variant="body1"
                    className="purple-text"
                    style={{ textAlign: 'center' }}
                >
                    You are about to be redirected to{' '}
                    <Link className="purple-text" as="button" type="submit">
                        {context.redirect}
                    </Link>
                    .
                </Text>
            </form>

            <Text
                variant="body2"
                className="purple-text"
                style={{ textAlign: 'center' }}
            >
                You&apos;re seeing this page because you are being redirected to
                a page that the login portal doesn&apos;t directly control
                access for. This is normal. However, if you keep returning to
                this page, it likely means that you don&apos;t have permission
                to access your destination.
            </Text>
        </Container>
    );
}

export default Redirect;
